import React, { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { Button, Select, DatePicker, message, Spin, Flex } from "antd";
import { CustomLabel } from "../../../utilities/utll";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiRequest } from "../../../utilities/apiMethod";
import DivisionWisePOGRNSummaryGrid from "./Grid";

const DivisionWisePOGRNSummaryReport = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [fromDate, setFromDate] = useState(dayjs(new Date()));
  const [toDate, setToDate] = useState(dayjs(new Date()));
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [gridData, setGridData] = useState([]);

  useEffect(() => {
    fetchPeriods();
    fetchDivisions();
  }, []);

  useEffect(() => {
    if (!gridData.length) handleFilter();
  }, [selectedPeriod, fromDate, toDate]);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("GET", "Period/getperiods");
      if (response?.IsSuccess && response.Data) {
        setPeriods(response.Data);
        handlePeriodChange(response.Data[0]?.PeriodId);
        messageApi.success(response.DisplayMessage || "Periods loaded successfully");
      } else {
        response?.ErrorMessage?.length > 0
          ? response.ErrorMessage.forEach((err) => messageApi.error(err))
          : messageApi.error(response?.DisplayMessage || "Failed to load periods");
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
      messageApi.error("An error occurred while fetching periods");
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await apiRequest("GET", "Category/getalldivisions");
      if (response?.IsSuccess && Array.isArray(response.Data) && response.Data.length > 0) {
        setDivisions(response.Data);
      }
    } catch (error) {
      console.error("Error fetching divisions:", error);
    }
  };

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    const selected = periods.find((p) => p.PeriodId === value);
    if (selected) {
      if (selected.PeriodName === "Custom") {
        setFromDate(null);
        setToDate(null);
      } else {
        setFromDate(dayjs(selected.FromDate));
        setToDate(dayjs(selected.Todate));
      }
    }
  };

  const handleFilter = async () => {
    if (!selectedPeriod) {
      messageApi.warning("Please select a period");
      return;
    }
    if (!fromDate || !toDate) {
      messageApi.warning("Please select both From Date and To Date");
      return;
    }
    try {
      setFilterLoading(true);
      const fromDateStr = fromDate.format("YYYY-MM-DD");
      const toDateStr = toDate.format("YYYY-MM-DD");

      let endpoint = `Reports/divisionWisePOGRNSummary?FromDate=${fromDateStr}&ToDate=${toDateStr}`;
      if (selectedDivision) endpoint += `&Division=${selectedDivision}`;

      const response = await apiRequest("GET", endpoint);
      if (response?.IsSuccess && response.Data) {
        if (response.Data.length > 0) {
          const formattedData = response.Data.map((val, index) => ({
            serialNo: index + 1,
            Division: val["Division"],
            DivisionName: val["DivisionName"],
            TotalPOValue: val["Total PO Value (In Lacs)"],
            GRNValue: val["GRN Value (In Lacs)"],
            GRNPendingValue: val["GRN Pending Value"],
            VendorDeliveredCount: val["Vendor Delivered Count"],
            VendorGRNPendingCount: val["Vendor GRN Pending Count"],
            ItemReceivedCount: val["Item Received Count"],
          }));
          setGridData(formattedData);
          messageApi.success(response.DisplayMessage || "Data retrieved successfully");
        } else {
          setGridData([]);
          messageApi.warning(response.DisplayMessage || "No records found");
        }
      } else {
        messageApi.error(response?.DisplayMessage || "An error occurred while fetching data");
      }
    } catch (error) {
      console.error("Error fetching Division Wise PO GRN Summary:", error);
      messageApi.error("An error occurred while fetching data");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedPeriod(null);
    setFromDate(null);
    setToDate(null);
    setSelectedDivision(null);
    setGridData([]);
  };

  const isCustomPeriod = () => {
    const selected = periods.find((p) => p.PeriodId === selectedPeriod);
    return selected && selected.PeriodName === "Custom";
  };

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Flex align="center" gap="middle">
          <Spin size="large" />
        </Flex>
      </div>
    );
  }

  return (
    <>
      {contextHolder}

      {/* Filter Card */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "16px 20px 16px",
          marginBottom: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #f0f0f0",
        }}
      >
        <Row className="g-3">
          <Col xl={3} md={6} xs={12}>
            <CustomLabel children={"Period"} isImportnt={false} />
            <Select
              id="period"
              placeholder="Select Period"
              allowClear
              showSearch
              optionFilterProp="label"
              size="middle"
              style={{ width: "100%" }}
              value={selectedPeriod}
              onChange={handlePeriodChange}
              options={periods.map((period) => ({
                value: period.PeriodId,
                label: period.PeriodName,
              }))}
            />
          </Col>

          <Col xl={3} md={6} xs={12}>
            <CustomLabel children={"From Date"} isImportnt={false} />
            <DatePicker
              format="DD-MMM-YYYY"
              placeholder="Select a date"
              style={{ width: "100%" }}
              size="middle"
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              disabled={!isCustomPeriod() && selectedPeriod !== null}
            />
          </Col>

          <Col xl={3} md={6} xs={12}>
            <CustomLabel children={"To Date"} isImportnt={false} />
            <DatePicker
              format="DD-MMM-YYYY"
              placeholder="Select a date"
              style={{ width: "100%" }}
              size="middle"
              value={toDate}
              onChange={(date) => setToDate(date)}
              disabled={!isCustomPeriod() && selectedPeriod !== null}
            />
          </Col>

          <Col xl={3} md={6} xs={12}>
            <CustomLabel children={"Division"} isImportnt={false} />
            <Select
              id="division"
              placeholder="All Divisions"
              allowClear
              showSearch
              optionFilterProp="label"
              size="middle"
              style={{ width: "100%" }}
              value={selectedDivision}
              onChange={(val) => setSelectedDivision(val)}
              options={divisions.map((div) => ({
                value: div.Division,
                label: `${div.Division} - ${div.DivisionName}`,
              }))}
            />
          </Col>
        </Row>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            icon={<ReloadOutlined />}
            size="middle"
            onClick={handleReset}
            style={{ borderColor: "#ff4d4f", color: "#ff4d4f", minWidth: 90 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            size="middle"
            onClick={handleFilter}
            loading={filterLoading}
            style={{
              backgroundColor: "#1aa8c4",
              borderColor: "#1a94c4",
              boxShadow: "0 2px 6px rgba(2, 2, 65, 0.25)",
              minWidth: 90,
            }}
          >
            Filter
          </Button>
        </div>
      </div>

      <DivisionWisePOGRNSummaryGrid data={gridData} />
    </>
  );
};

export default DivisionWisePOGRNSummaryReport;
